import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuid } from "uuid";
import { toast } from "react-toastify";
import { authService } from "../../../Service/Axios";
import {
  DELETE_EMAIL_WORKFLOW_URL,
  CREATE_EMAIL_WORKFLOW_URL,
  GET_QUESTION_LIST_BY_PROJECT_URL,
  GET_OPERATOR_LIST_BY_PROJECT_QUESTIONS_URL,
  GET_VALUES_LIST_BY_PROJECT_QUESTIONS_URL,
  GET_EMAIL_WORKFLOW_DETAILS_URL,
  UPDATE_EMAIL_WORKFLOW_URL,
  GET_PROJECT_USER_LIST_URL,
} from "../../../Constant/api";
import {
  useEmailTemplateList,
  useEmailRoleList,
  useProjectList,
  useEmailWorkflowList,
} from "../../../Constant/apiHooks";
import { MultiCheckDropdown } from "../../MultiCheckDropdown";
import { LoadingComponent } from "../../LoadingComponent";
import { ButtonLoader } from "../../ButtonLoader";
import { Validator } from "../../../Utils/validator";
import { formatDateWithFullMonth } from "../../../Utils";

export function EmailSettings() {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [specificSettingLoading, setSpecificSettingLoading] = useState(false);
  const [isRuleOpen, setIsRuleOpen] = useState(false);
  const [questionsData, setQuestionData] = useState(null);
  const [questionList, setQuestionList] = useState([]);
  const [userList, setUserList] = useState([]);

  const [formData, setFormData] = useState({
    name: "",
    projectId: "",
    templateId: "",
    frequency: "",
    ruleStatus: true,
    frequencyRule: "",
    frequencyRepeatEvery: "",
    frequencyRepeatNumber: null,
    frequencyEnds: "",
    frequencyEndsOccurrence: null,
    frequencyEndsAfter: "",
    frequencyEndsTime: null,
    gid: "",
    to: "",
    toRoles: [],
    toApplicant: [],
    cc: "",
    ccRoles: [],
    ccApplicant: [],
  });
  const {
    name,
    projectId,
    templateId,
    frequency,
    ruleStatus,
    frequencyRule,
    frequencyRepeatEvery,
    frequencyRepeatNumber,
    frequencyEnds,
    frequencyEndsOccurrence,
    frequencyEndsAfter,
    frequencyEndsTime,
    to,
    toRoles,
    toApplicant,
    cc,
    ccRoles,
    ccApplicant,
  } = formData;

  const [errMsg, setErrMsg] = useState({
    name: "",
    projectId: "",
    templateId: "",
    frequency: "",
  });

  const defaultIds = {
    mainConditionsId: uuid(),
    questionId: uuid(),
  };
  const defaultQuestionsCondions = {
    id: defaultIds.questionId,
    condition: "AND",
    questionId: "",
    operator: "",
    values: [],
  };
  const defaultMainConditions = {
    id: defaultIds.mainConditionsId,
    condition: "AND",
    questions: [
      {
        ...defaultQuestionsCondions,
      },
    ],
    if: "ALL",
  };

  const [mainConditions, setMainConditions] = useState([
    {
      ...defaultMainConditions,
    },
  ]);

  const { projectList } = useProjectList();
  const { allEmailTemplateList } = useEmailTemplateList();

  const { allEmailWorkflowList, fetchAllEmailWorkflowList } =
    useEmailWorkflowList();
  const { emailRoleList } = useEmailRoleList();

  // Toggle
  const toggleRule = () => {
    onResetRules();
    setIsRuleOpen(!isRuleOpen);
  };

  // Listing
  const modifiedProjectList = useMemo(() => {
    return projectList?.map((project) => ({
      label: project.name,
      value: project.gid,
    }));
  }, [projectList]);

  const modifiedEmailRoleList = useMemo(() => {
    return emailRoleList?.map((role) => ({
      label: role.name || "",
      value: role.roleId,
    }));
  }, [emailRoleList]);

  const modifiedUserList = useMemo(() => {
    return userList?.map((user) => ({
      label:
        user.firstName && user.lastName
          ? `${user.firstName} ${user.lastName}`
          : "",
      value: user.userId,
    }));
  }, [userList]);

  const getSpecficWorkflow = async (gid) => {
    try {
      onResetRules();
      setSpecificSettingLoading(true);
      const payload = {
        gid,
      };
      const response = await authService(
        GET_EMAIL_WORKFLOW_DETAILS_URL,
        "POST",
        payload
      );
      if (response) {
        setFormData((prevData) => ({
          ...prevData,
          gid: response.gid,
          name: response.name,
          projectId: response.projectId,
          templateId: response.action?.then,
          ruleStatus: response.activate,
          frequency: response.action?.emailFrequency?.frequency,
          frequencyRule: response.action?.emailFrequency?.rule,
          frequencyRepeatEvery: response.action?.emailFrequency?.repeatEvery,
          frequencyRepeatNumber: response.action?.emailFrequency?.repeatNumber,
          frequencyEnds: response.action?.emailFrequency?.ends,
          frequencyEndsOccurrence: response.action?.emailFrequency?.interval,
          frequencyEndsAfter: response.action?.emailFrequency?.endsAfter,
          frequencyEndsTime: response.action?.emailFrequency?.endsNumber,
          to: response.to,
          toRoles:
            modifiedEmailRoleList &&
            modifiedEmailRoleList?.filter((obj) =>
              response.toRoles?.includes(obj.value)
            ),
          toApplicant:
            modifiedUserList &&
            modifiedUserList?.filter((obj) =>
              response.toApplicant?.includes(obj.value)
            ),
          cc: response.cc,
          ccRoles:
            modifiedEmailRoleList &&
            modifiedEmailRoleList?.filter((obj) =>
              response.ccRoles?.includes(obj.value)
            ),
          ccApplicant:
            modifiedUserList &&
            modifiedUserList?.filter((obj) =>
              response.ccApplicant?.includes(obj.value)
            ),
        }));
        setMainConditions(response.mainConditions);
        getQuestionList(response.projectId);
        getUserList(response.projectId);
        for (const condition of response.mainConditions) {
          const { questions } = condition;
          for (const question of questions) {
            const { questionId, id } = question;
            getQuestionData(response.projectId, questionId, id);
          }
        }
        setIsRuleOpen(true);
        setSpecificSettingLoading(false);
      }
    } catch (error) {
      toast.error(t("common.messages.notification.failed.somethingWentWrong"));
    }
  };

  const getUserList = async (projectId) => {
    try {
      const payload = {
        projectId,
      };
      const response = await authService(
        GET_PROJECT_USER_LIST_URL,
        "POST",
        payload
      );
      setUserList(response);
    } catch (error) {
      toast.error("Error fetching user list:", error);
    }
  };

  const getQuestionList = async (projectId) => {
    try {
      const payload = {
        gid: projectId,
      };
      const response = await authService(
        GET_QUESTION_LIST_BY_PROJECT_URL,
        "POST",
        payload
      );
      if (response) {
        setQuestionList(response);
      }
    } catch (e) {
      toast.error(e);
    }
  };

  const getQuestionData = async (projectId, questionId, qId) => {
    setLoading(true);
    const payload = {
      questionId,
      projectId,
    };
    if (questionId && projectId) {
      const operatorResponse = await authService(
        GET_OPERATOR_LIST_BY_PROJECT_QUESTIONS_URL,
        "POST",
        payload
      );

      const valuesResponse = await authService(
        GET_VALUES_LIST_BY_PROJECT_QUESTIONS_URL,
        "POST",
        payload
      );
      if (operatorResponse && valuesResponse) {
        const newObject = {
          [qId]: {
            values: valuesResponse,
            operator: operatorResponse,
          },
        };
        setQuestionData((prevQuestionData) => ({
          ...prevQuestionData,
          ...newObject,
        }));
      } else {
        const newObject = {
          [qId]: {
            values: null,
            operator: [],
          },
        };
        setQuestionData((prevQuestionData) => ({
          ...prevQuestionData,
          ...newObject,
        }));
      }
    }
    setLoading(false);
  };

  // On Change
  const handleChange = (e) => {
    if (e.target.type === "checkbox") {
      setFormData((prev) => ({
        ...prev,
        [e.target.name]: e.target.checked,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [e.target.name]: e.target.value,
      }));
      if (e.target.name === "name") {
        if (!Validator.text(e.target.value)) {
          setErrMsg((prev) => ({
            ...prev,
            name: t("common.messages.error.email.rules.name.blank"),
          }));
        } else {
          setErrMsg((prev) => ({
            ...prev,
            name: "",
          }));
        }
      }
      if (e.target.name === "projectId") {
        getQuestionList(e.target.value);
        getUserList(e.target.value);
        if (!Validator.text(e.target.value)) {
          setErrMsg((prev) => ({
            ...prev,
            projectId: t("common.messages.error.email.rules.projectId.blank"),
          }));
        } else {
          setErrMsg((prev) => ({
            ...prev,
            projectId: "",
          }));
        }
      }
      if (e.target.name === "templateId") {
        if (!Validator.text(e.target.value)) {
          setErrMsg((prev) => ({
            ...prev,
            templateId: t("common.messages.error.email.rules.templateId.blank"),
          }));
        } else {
          setErrMsg((prev) => ({
            ...prev,
            templateId: "",
          }));
        }
      }
      if (e.target.name === "frequency") {
        if (!Validator.text(e.target.value)) {
          setErrMsg((prev) => ({
            ...prev,
            frequency: t("common.messages.error.email.rules.frequency.blank"),
          }));
        } else {
          setErrMsg((prev) => ({
            ...prev,
            frequency: "",
          }));
        }
      }
    }
  };

  const handleAddMainConditions = () => {
    setMainConditions([...mainConditions, defaultMainConditions]);
  };

  const handleDeleteMainCondition = (id) => {
    const deletedArr = mainConditions.filter((e) => e.id !== id);
    setMainConditions(deletedArr);
  };

  const handleAddQuestionsConditions = (index) => {
    const newArr = [...mainConditions];
    newArr[index].questions.push(defaultQuestionsCondions);
    setMainConditions(newArr);
  };

  const handleDeleteQuestionsCondition = (mainId, questionId) => {
    const updatedQuestionData = { ...questionsData };
    delete updatedQuestionData[questionId];
    setQuestionData(updatedQuestionData);
    const updatedData = mainConditions.map((item) => {
      if (item.id === mainId) {
        const updatedQuestions = item.questions.filter(
          (question) => question.id !== questionId
        );
        return { ...item, questions: updatedQuestions };
      }
      return item;
    });
    setMainConditions(updatedData);
  };

  const handleChangeMainConditions = (e, index) => {
    const newArr = [...mainConditions];
    newArr[index][e.target.name] = e.target.value;
    setMainConditions(newArr);
  };

  const handleChangeQuestionsConditions = (
    e,
    mainIndex,
    quesIndex,
    value,
    questionId
  ) => {
    const newArr = [...mainConditions];
    newArr[mainIndex].questions[quesIndex][e.target.name] = value;
    if (e.target.name === "questionId") {
      getQuestionData(projectId, value, questionId);
      newArr[mainIndex].questions[quesIndex].operator = "";
    }
    setMainConditions(newArr);
  };

  const handleChangeQuestionsConditionsMultipleValues = (
    values,
    mainIndex,
    quesIndex
  ) => {
    const modifiedValues = values.map((option) => option.value);
    const newArr = [...mainConditions];
    newArr[mainIndex].questions[quesIndex].values = modifiedValues;
    setMainConditions(newArr);
  };

  const questionValuesRender = (
    values,
    index,
    quesCond,
    quesIndex,
    selectValues
  ) => {
    if (values.designType === "select") {
      const options =
        values.dropdownValue &&
        values.dropdownValue.length > 0 &&
        values.dropdownValue.map((option) => ({
          value: option.value,
          label: option.name || "",
        }));
      const selectedValue =
        options && options.filter((obj) => selectValues.includes(obj.value));
      return (
        <MultiCheckDropdown
          options={options}
          value={selectedValue}
          onChange={(values) =>
            handleChangeQuestionsConditionsMultipleValues(
              values,
              index,
              quesIndex
            )
          }
        />
      );
    }
    return (
      <input
        type={values.designType}
        className="form-control form-control-md"
        name="values"
        value={quesCond.values}
        onChange={(e) =>
          handleChangeQuestionsConditions(e, index, quesIndex, [e.target.value])
        }
      />
    );
  };

  const handleChangeMultiple = (selected, name) => {
    setFormData((prev) => ({
      ...prev,
      [name]: selected,
    }));
  };

  // Validation
  const getValidation = () => {
    return new Promise((resolve) => {
      if (
        Validator.text(name) &&
        Validator.text(projectId) &&
        Validator.text(templateId) &&
        Validator.text(frequency)
      ) {
        resolve(true);
      } else {
        if (!Validator.text(name)) {
          setErrMsg((prev) => ({
            ...prev,
            name: t("common.messages.error.email.rules.name.blank"),
          }));
        }

        if (!Validator.text(projectId)) {
          setErrMsg((prev) => ({
            ...prev,
            projectId: t("common.messages.error.email.rules.projectId.blank"),
          }));
        }

        if (!Validator.text(templateId)) {
          setErrMsg((prev) => ({
            ...prev,
            templateId: t("common.messages.error.email.rules.templateId.blank"),
          }));
        }

        if (!Validator.text(frequency)) {
          setErrMsg((prev) => ({
            ...prev,
            frequency: t("common.messages.error.email.rules.frequency.blank"),
          }));
        }
        resolve(false);
      }
    });
  };

  // Submit
  const onSaveRules = async (e) => {
    e.preventDefault();
    const validationStatus = await getValidation();
    if (validationStatus) {
      setLoading(true);
      try {
        const payload = {
          name: name,
          projectId: projectId,
          activate: ruleStatus,
          action: {
            then: templateId,
            emailFrequency: {
              frequency: frequency,
              rule: frequencyRule,
              repeatEvery: frequencyRepeatEvery,
              repeatNumber: frequencyRepeatNumber
                ? Number(frequencyRepeatNumber)
                : null,
              ends: frequencyEnds,
              interval: frequencyEndsOccurrence
                ? Number(frequencyEndsOccurrence)
                : null,
              endsAfter: frequencyEndsAfter,
              endsNumber: frequencyEndsTime ? Number(frequencyEndsTime) : null,
            },
          },
          mainConditions,
          to,
          cc,
          toRoles: toRoles?.map((option) => option.value),
          toApplicant: toApplicant?.map((option) => option.value),
          ccRoles: ccRoles?.map((option) => option.value),
          ccApplicant: ccApplicant?.map((option) => option.value),
        };
        if (formData.gid) {
          payload.gid = formData.gid;
          const response = await authService(
            UPDATE_EMAIL_WORKFLOW_URL,
            "POST",
            payload
          );
          if (response.success) {
            toast.success(
              t("common.messages.notification.success.updateEmailRule")
            );
            fetchAllEmailWorkflowList();
            onResetRules();
          } else {
            toast.success(response.message);
          }
        } else {
          const response = await authService(
            CREATE_EMAIL_WORKFLOW_URL,
            "POST",
            payload
          );
          if (response.success) {
            toast.success(
              t("common.messages.notification.success.createEmailRule")
            );
            fetchAllEmailWorkflowList();
            onResetRules();
          } else {
            toast.success(response.message);
          }
        }
        setLoading(false);
      } catch (error) {
        setLoading(false);
        if (error.response && error.response.data) {
          toast.error(error.response.data.message);
        }
      }
    }
  };

  const onDeleteRule = async (id) => {
    try {
      const data = {
        gid: id,
      };
      const response = await authService(
        DELETE_EMAIL_WORKFLOW_URL,
        "POST",
        data
      );
      if (response.success) {
        fetchAllEmailWorkflowList();
        toast.success(
          t("common.messages.notification.success.deleteEmailRule")
        );
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error(t("common.messages.notification.failed.somethingWentWrong"));
    }
  };

  // Reset
  const onResetRules = () => {
    setIsRuleOpen(false);
    setFormData((prevData) => ({
      ...prevData,
      gid: "",
      name: "",
      projectId: "",
      templateId: "",
      ruleStatus: true,
      frequency: "",
      frequencyRule: "",
      frequencyRepeatEvery: "",
      frequencyRepeatNumber: null,
      frequencyEnds: "",
      frequencyEndsOccurrence: null,
      frequencyEndsAfter: "",
      frequencyEndsTime: null,
      to: "",
      toRoles: [],
      toApplicant: [],
      cc: "",
      ccRoles: [],
      ccApplicant: [],
    }));
    setMainConditions([defaultMainConditions]);
    setQuestionList([]);
    setQuestionData(null);
  };

  return (
    <div className="">
      <LoadingComponent isLoading={specificSettingLoading}>
        {!isRuleOpen && (
          <div className="email-nasting">
            <div className="inbox-search mb-3 p-4 bg-slate-50">
              <div className="row">
                <div className="col pe-2">
                  <div className="search-div">
                    <div className="input-group">
                      <span className="input-group-text">
                        <i className="pluto-search"></i>
                      </span>
                      <input
                        type="search"
                        className="form-control"
                        placeholder={t("common.placeHolder.searchRule")}
                      />
                    </div>
                  </div>
                </div>
                <div className="col-auto">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={toggleRule}
                  >
                    {t("common.button.createNewRule")}{" "}
                    <i className="bx bx-plus bx-sm align-middle"></i>
                  </button>
                </div>
              </div>
            </div>
            <>
              <div className="email-result-list px-4">
                <h3>{t("auth.email.settings.title")}</h3>
              </div>
              <div className="email-details">
                <div className="email-list email-settings bg-white">
                  <ul>
                    {allEmailWorkflowList &&
                      allEmailWorkflowList.length > 0 &&
                      allEmailWorkflowList.map((wrkflw) => (
                        <li
                          key={wrkflw.gid}
                          onClick={() => getSpecficWorkflow(wrkflw.gid)}
                        >
                          <div className="email-list-box d-flex">
                            <div className="read-view">
                              <button
                                type="button"
                                className="button"
                                onClick={() => onDeleteRule(wrkflw.gid)}
                              >
                                <i className="bx bx-trash"></i>
                              </button>
                            </div>
                            <div className="email-short-details">
                              <div className="emailer-name">
                                {t("common.label.createdOn")}{" "}
                                <span className="date">
                                  {wrkflw._id?.date &&
                                    formatDateWithFullMonth(wrkflw._id?.date)}
                                </span>
                              </div>
                              <div className="email-title">{wrkflw.name}</div>
                              <div className="setting-short-content">
                                <p>
                                  It is a long established fact that a reader
                                  will be distracted by the readable content of
                                  a page when looking at its layout. The point
                                  of using Lorem Ipsum is that it has a
                                  more-or-less normal distribution of letters.
                                </p>
                              </div>
                            </div>
                          </div>
                        </li>
                      ))}
                  </ul>
                </div>
              </div>
            </>
          </div>
        )}

        {/* New Rule */}
        {isRuleOpen && (
          <div className="email-details">
            <div className="p-3 d-flex align-items-center border-bottom">
              <button
                onClick={onResetRules}
                className="btn btn-back bg-transparent d-flex justify-content-center align-items-center border-0"
              >
                <i className="bx bx-arrow-back bx-sm"></i>
              </button>
              <h3 className="h6 mb-0 ms-2">
                {t("auth.email.settings.addRule")}
              </h3>
            </div>
            <div className="email-form p-4">
              <form onSubmit={onSaveRules}>
                <div className="row">
                  <div className="col-lg-5 col-md-5 col-sm-6 col-12">
                    <div className="mb-4">
                      <label>{t("common.label.name")}</label>
                      <input
                        className="form-control form-control-md"
                        type="text"
                        id="name"
                        name="name"
                        value={name}
                        onChange={handleChange}
                      />
                      {errMsg.name && <p className="error">{errMsg.name}</p>}
                    </div>
                  </div>
                  <div className="col-lg-5 col-md-5 col-sm-6 col-12">
                    <div className="mb-4">
                      <label>{t("common.label.chooseProject")}</label>
                      <select
                        className="form-select form-select-md"
                        name="projectId"
                        value={projectId}
                        onChange={handleChange}
                      >
                        <option>{t("common.label.select")}</option>
                        {modifiedProjectList &&
                          modifiedProjectList.length > 0 &&
                          modifiedProjectList.map((prjct) => (
                            <option key={prjct.value} value={prjct.value}>
                              {prjct.label}
                            </option>
                          ))}
                      </select>
                      {errMsg.projectId && (
                        <p className="error">{errMsg.projectId}</p>
                      )}
                    </div>
                  </div>
                  <div className="col-lg-2 col-md-2 col-sm-6 col-12">
                    <div className="mb-4">
                      <label>{t("common.label.enable")}</label>
                      <div className="form-check form-switch form-check-success">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          role="switch"
                          id="ruleStatus"
                          name="ruleStatus"
                          checked={ruleStatus}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-xl-3 col-lg-3 col-md-12 col-sm-12 col-12">
                    <div className="mb-4">
                      <label>{t("common.label.to")}</label>
                      <div>
                        <div className="form-check form-check-inline">
                          <input
                            className="form-check-input"
                            type="radio"
                            id="to"
                            name="to"
                            value="byRole"
                            checked={to === "byRole"}
                            onChange={handleChange}
                          />
                          <label className="form-check-label">
                            {t("common.label.byRole")}
                          </label>
                        </div>
                      </div>
                      <div className="mb-4">
                        <label>{t("common.label.chooseRole")}</label>
                        <MultiCheckDropdown
                          placeholder={t("common.placeHolder.searchRule")}
                          options={modifiedEmailRoleList}
                          value={toRoles}
                          onChange={(selected) =>
                            handleChangeMultiple(selected, "toRoles")
                          }
                          disabled={to !== "byRole"}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-xl-3 col-lg-3 col-md-12 col-sm-12 col-12">
                    <div className="mb-4">
                      <label></label>
                      <div>
                        <div className="form-check form-check-inline">
                          <input
                            className="form-check-input"
                            type="radio"
                            id="to"
                            name="to"
                            value="byApplicant"
                            checked={to === "byApplicant"}
                            onChange={handleChange}
                          />
                          <label className="form-check-label">
                            {t("common.label.byApplicant")}
                          </label>
                        </div>
                      </div>
                      <div className="mb-4">
                        <label>{t("common.label.chooseApplicant")}</label>
                        <MultiCheckDropdown
                          placeholder={t("common.placeHolder.searchApplicant")}
                          options={modifiedUserList}
                          value={toApplicant}
                          onChange={(selected) =>
                            handleChangeMultiple(selected, "toApplicant")
                          }
                          disabled={to !== "byApplicant"}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-xl-3 col-lg-3 col-md-12 col-sm-12 col-12">
                    <div className="mb-4">
                      <label>{t("common.label.cc")}</label>
                      <div>
                        <div className="form-check form-check-inline">
                          <input
                            className="form-check-input"
                            type="radio"
                            id="cc"
                            name="cc"
                            value="byRole"
                            checked={cc === "byRole"}
                            onChange={handleChange}
                          />
                          <label className="form-check-label">
                            {t("common.label.byRole")}
                          </label>
                        </div>
                      </div>
                      <div className="mb-4">
                        <label>{t("common.label.chooseRole")}</label>
                        <div>
                          <MultiCheckDropdown
                            placeholder={t("common.placeHolder.searchRule")}
                            options={modifiedEmailRoleList}
                            value={ccRoles}
                            onChange={(selected) =>
                              handleChangeMultiple(selected, "ccRoles")
                            }
                            disabled={cc !== "byRole"}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-xl-3 col-lg-3 col-md-12 col-sm-12 col-12">
                    <div className="mb-4">
                      <label></label>
                      <div>
                        <div className="form-check form-check-inline">
                          <input
                            className="form-check-input"
                            type="radio"
                            id="cc"
                            name="cc"
                            value="byApplicant"
                            checked={cc === "byApplicant"}
                            onChange={handleChange}
                          />
                          <label className="form-check-label">
                            {t("common.label.byEmployee")}
                          </label>
                        </div>
                      </div>
                      <div className="mb-4">
                        <label>{t("common.label.chooseApplicant")}</label>
                        <MultiCheckDropdown
                          placeholder={t("common.placeHolder.searchApplicant")}
                          options={modifiedUserList}
                          value={ccApplicant}
                          onChange={(selected) =>
                            handleChangeMultiple(selected, "ccApplicant")
                          }
                          disabled={cc !== "byApplicant"}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {mainConditions &&
                  mainConditions.length > 0 &&
                  mainConditions.map((mainCond, index) => (
                    <div className="if-wrapper" key={mainCond.id}>
                      <div className="step-row mb-4">
                        <div className="d-flex justify-content-between align-items-center">
                          <label>{t("common.label.if")}</label>
                          {index !== 0 && (
                            <button
                              type="button"
                              className="delete-btn"
                              onClick={() =>
                                handleDeleteMainCondition(mainCond.id)
                              }
                            >
                              <i className="bx bx-x"></i>
                            </button>
                          )}
                        </div>

                        <div className="row align-items-center">
                          {index !== 0 && (
                            <div className="col-lg-5 col-md-5 col-sm-6 col-12">
                              <div className="mb-4">
                                <select
                                  className="form-select form-select-md"
                                  name="condition"
                                  value={mainCond.condition}
                                  onChange={(e) =>
                                    handleChangeMainConditions(e, index)
                                  }
                                >
                                  <option value="AND">
                                    {t("common.label.and")}
                                  </option>
                                  <option value="OR">
                                    {t("common.label.or")}
                                  </option>
                                </select>
                              </div>
                            </div>
                          )}
                          <div className="col-lg-5 col-md-5 col-sm-6 col-12">
                            <div className="mb-4">
                              <select
                                className="form-select form-select-md"
                                name="if"
                                value={mainCond.if}
                                onChange={(e) =>
                                  handleChangeMainConditions(e, index)
                                }
                              >
                                <option value="ALL">
                                  {t("common.label.all")}
                                </option>
                                <option value="ANY">
                                  {t("common.label.any")}
                                </option>
                              </select>
                            </div>
                          </div>
                          <div className="col-lg-5 col-md-5 col-sm-6 col-12">
                            <div className="mb-4">
                              <strong>{t("auth.email.settings.ifRule")}</strong>
                            </div>
                          </div>
                        </div>
                        {mainCond.questions &&
                          mainCond.questions.length > 0 &&
                          mainCond.questions.map((quesCond, quesIndex) => (
                            <div className="add-row-wrapper" key={quesCond.id}>
                              <div className="row">
                                <div className="col-lg-4 col-md-5 col-sm-6 col-12">
                                  <div className="mb-4">
                                    <select
                                      className="form-select form-select-md"
                                      name="questionId"
                                      value={quesCond.questionId}
                                      onChange={(e) =>
                                        handleChangeQuestionsConditions(
                                          e,
                                          index,
                                          quesIndex,
                                          e.target.value,
                                          quesCond.id
                                        )
                                      }
                                    >
                                      <option value="">Select</option>
                                      {questionList &&
                                        questionList.length > 0 &&
                                        questionList.map((qstn) => (
                                          <option
                                            value={qstn.questionId}
                                            key={qstn.questionId}
                                          >
                                            {qstn.name}
                                          </option>
                                        ))}
                                    </select>
                                  </div>
                                </div>
                                <div className="col-lg-3 col-md-5 col-sm-6 col-12">
                                  <div className="mb-4">
                                    <select
                                      className="form-select form-select-md"
                                      name="operator"
                                      value={quesCond.operator}
                                      onChange={(e) =>
                                        handleChangeQuestionsConditions(
                                          e,
                                          index,
                                          quesIndex,
                                          e.target.value
                                        )
                                      }
                                    >
                                      <option>
                                        {t("common.label.select")}
                                      </option>
                                      {questionsData &&
                                        questionsData[quesCond.id] &&
                                        questionsData[quesCond.id].operator &&
                                        questionsData[quesCond.id].operator
                                          .length > 0 &&
                                        questionsData[quesCond.id].operator.map(
                                          (oprtor) => {
                                            return (
                                              <option
                                                value={oprtor.name}
                                                key={oprtor.name}
                                              >
                                                {oprtor.name}
                                              </option>
                                            );
                                          }
                                        )}
                                    </select>
                                  </div>
                                </div>
                                <div className="col-lg-3 col-md-5 col-sm-6 col-12">
                                  <div className="mb-4">
                                    {questionsData &&
                                    questionsData[quesCond.id] &&
                                    questionsData[quesCond.id].values ? (
                                      questionValuesRender(
                                        questionsData[quesCond.id].values,
                                        index,
                                        quesCond,
                                        quesIndex,
                                        quesCond.values
                                      )
                                    ) : (
                                      <input
                                        type="text"
                                        disabled
                                        className="form-control form-control-md"
                                      />
                                    )}
                                  </div>
                                </div>
                                <div className="col-lg-2 col-md-5 col-sm-6 col-12">
                                  <div className="d-flex gap-2 mb-4">
                                    <button
                                      type="button"
                                      className="delete-btn"
                                      onClick={() =>
                                        handleAddQuestionsConditions(index)
                                      }
                                    >
                                      <i className="bx bx-plus"></i>
                                    </button>

                                    <button
                                      type="button"
                                      className="delete-btn"
                                      onClick={() =>
                                        handleDeleteQuestionsCondition(
                                          mainCond.id,
                                          quesCond.id
                                        )
                                      }
                                    >
                                      <i className="bx bx-x"></i>
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                      </div>
                    </div>
                  ))}
                <div className="col-lg-4 col-md-5 col-sm-6 col-12">
                  <div className="bachcolor-color">
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-block d-flex justify-content-between align-items-center"
                      onClick={handleAddMainConditions}
                    >
                      {t("common.label.and")}/{t("common.label.or")}{" "}
                      <i className="bx bx-plus bx-sm"></i>
                    </button>
                  </div>
                </div>
                <div className="then-wrapper mt-4">
                  <div className="add-row-wrapper">
                    <div className="step-row ">
                      <label>{t("common.label.then")}</label>
                      <div className="row">
                        <div className="col-lg-6 col-md-6 col-sm-6 col-12">
                          <div className="mb-4 mb-sm-0">
                            <select
                              className="form-select form-select-md"
                              name="templateId"
                              value={templateId}
                              onChange={handleChange}
                            >
                              <option>{t("common.label.select")}</option>
                              {allEmailTemplateList &&
                                allEmailTemplateList.length > 0 &&
                                allEmailTemplateList.map((template) => (
                                  <option
                                    key={template.gid}
                                    value={template.gid}
                                  >
                                    {template.name}
                                  </option>
                                ))}
                            </select>
                            {errMsg.templateId && (
                              <p className="error">{errMsg.templateId}</p>
                            )}
                          </div>
                        </div>
                        <div className="col-lg-6 col-md-6 col-sm-6 col-12">
                          <div className="mb-4">
                            <select
                              className="form-select form-select-md"
                              name="frequency"
                              value={frequency}
                              onChange={handleChange}
                            >
                              <option>{t("common.label.select")}</option>
                              <option value="oneTime">
                                {t("common.label.oneTime")}
                              </option>
                              <option value="recuring">
                                {t("common.label.recuring")}
                              </option>
                            </select>
                            {errMsg.frequency && (
                              <p className="error">{errMsg.frequency}</p>
                            )}
                          </div>
                        </div>
                        {frequency === "recuring" && (
                          <>
                            <div className="col-xxl-6 col-xl-6 col-lg-6 col-md-12 col-sm-12 col-12">
                              <div className="mb-4">
                                <label>{t("common.label.frequencyRule")}</label>
                                <div>
                                  <div className="form-check form-check-inline">
                                    <input
                                      className="form-check-input"
                                      type="radio"
                                      id="frequencyRule"
                                      name="frequencyRule"
                                      value="static"
                                      checked={frequencyRule === "static"}
                                      onChange={handleChange}
                                    />
                                    <label className="form-check-label">
                                      {t("common.label.static")}
                                    </label>
                                  </div>
                                  <div className="form-check form-check-inline">
                                    <input
                                      className="form-check-input"
                                      type="radio"
                                      id="frequencyRule"
                                      name="frequencyRule"
                                      value="dynamic"
                                      checked={frequencyRule === "dynamic"}
                                      onChange={handleChange}
                                    />
                                    <label className="form-check-label">
                                      {t("common.label.dynamic")}
                                    </label>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="col-xxl-6 col-xl-6 col-lg-6 col-md-12 col-sm-12 col-12">
                              <div className="mb-4">
                                <label>
                                  {t("common.label.frequencyRepeatEvery")}
                                </label>
                                <div className="row">
                                  <div className="col">
                                    <input
                                      className="form-control"
                                      type="number"
                                      id="frequencyRepeatNumber"
                                      name="frequencyRepeatNumber"
                                      value={frequencyRepeatNumber}
                                      onChange={handleChange}
                                    />
                                  </div>
                                  <div className="col">
                                    <select
                                      className="form-select"
                                      name="frequencyRepeatEvery"
                                      value={frequencyRepeatEvery}
                                      onChange={handleChange}
                                    >
                                      <option>
                                        {t("common.label.select")}
                                      </option>
                                      <option value="day">
                                        {t("common.label.day")}
                                      </option>
                                      <option value="week">
                                        {t("common.label.week")}
                                      </option>
                                      <option value="month">
                                        {t("common.label.month")}
                                      </option>
                                      <option value="year">
                                        {t("common.label.year")}
                                      </option>
                                    </select>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="col-xxl-12 col-xl-12 col-lg-12 col-md-12 col-sm-12 col-12">
                              <div className="mb-4">
                                <label>{t("common.label.frequencyEnds")}</label>
                                <div className="flex">
                                  <div className="form-check form-check-inline">
                                    <input
                                      className="form-check-input"
                                      type="radio"
                                      id="frequencyEnds"
                                      name="frequencyEnds"
                                      value="never"
                                      checked={frequencyEnds === "never"}
                                      onChange={handleChange}
                                    />
                                    <label className="form-check-label">
                                      {t("common.label.never")}
                                    </label>
                                  </div>
                                  <div className="form-check form-check-inline">
                                    <input
                                      className="form-check-input"
                                      type="radio"
                                      id="frequencyEnds"
                                      name="frequencyEnds"
                                      value="custom"
                                      checked={frequencyEnds === "custom"}
                                      onChange={handleChange}
                                    />
                                    <label className="form-check-label">
                                      {t("common.label.custom")}
                                    </label>
                                  </div>
                                  <div className="form-check form-check-inline">
                                    <input
                                      className="form-check-input"
                                      type="radio"
                                      id="frequencyEnds"
                                      name="frequencyEnds"
                                      value="after"
                                      checked={frequencyEnds === "after"}
                                      onChange={handleChange}
                                    />
                                    <label className="form-check-label">
                                      {t("common.label.after")}
                                    </label>
                                    <span className="form-check form-check-inline">
                                      <input
                                        className="form-control max-w-96"
                                        type="number"
                                        id="frequencyEndsOccurrence"
                                        name="frequencyEndsOccurrence"
                                        value={frequencyEndsOccurrence}
                                        onChange={handleChange}
                                        disabled={frequencyEnds !== "after"}
                                      />
                                    </span>
                                  </div>
                                  <div className="form-check form-check-inline">
                                    <input
                                      className="form-check-input"
                                      type="radio"
                                      id="frequencyEnds"
                                      name="frequencyEnds"
                                      value="post"
                                      checked={frequencyEnds === "post"}
                                      onChange={handleChange}
                                    />
                                    <label className="form-check-label">
                                      {t("common.label.post")}
                                    </label>

                                    <span className="form-check form-check-inline">
                                      <div className="row">
                                        <div className="col">
                                          <input
                                            className="form-control"
                                            type="number"
                                            id="frequencyEndsTime"
                                            name="frequencyEndsTime"
                                            value={frequencyEndsTime}
                                            onChange={handleChange}
                                            disabled={frequencyEnds !== "post"}
                                          />
                                        </div>
                                        <div className="col">
                                          <select
                                            className="form-select"
                                            name="frequencyEndsAfter"
                                            value={frequencyEndsAfter}
                                            onChange={handleChange}
                                            disabled={frequencyEnds !== "post"}
                                          >
                                            <option>
                                              {t("common.label.select")}
                                            </option>
                                            <option value="day">
                                              {t("common.label.day")}
                                            </option>
                                            <option value="week">
                                              {t("common.label.week")}
                                            </option>
                                            <option value="month">
                                              {t("common.label.month")}
                                            </option>
                                            <option value="year">
                                              {t("common.label.year")}
                                            </option>
                                          </select>
                                        </div>
                                      </div>
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="then-wrapper mt-5">
                  <div className="gap-2 d-flex">
                    <button
                      type="button"
                      className="btn btn-light ms-auto"
                      onClick={onResetRules}
                    >
                      {t("common.button.cancel")}
                    </button>
                    {!loading ? (
                      <button type="submit" className="btn btn-primary">
                        {t("common.button.save")}
                      </button>
                    ) : (
                      <ButtonLoader />
                    )}
                  </div>
                </div>
              </form>
            </div>
          </div>
        )}
      </LoadingComponent>
    </div>
  );
}
